'use client';

import { Suspense, useEffect, useState, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import toast from 'react-hot-toast';
import styles from '../styles/Dashboard.module.css';
import Stage2Camera from '../components/Stage2Camera';
import DateTimePicker from '../components/DateTimePicker';
import { generateRoomId } from '@/lib/client-utils';

interface Booking {
  id: string;
  email: string;
  jobTitle: string;
  name: string;
  timeSlot: string;
  resumePath: string;
  imagePaths?: string[];
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  roomId?: string;
  deletedAt?: number;
  deletionReason?: string;
  timeChangeRequest?: {
    requestedTimeSlot: string;
    status: 'pending' | 'approved' | 'rejected';
  };
  malpracticeDetected?: string;
  createdAt: number;
  updatedAt: number;
}

const STAGE_STEPS = [
  {
    id: 1,
    title: 'Interview Details',
    subtitle: 'Candidate & slot information',
  },
  {
    id: 2,
    title: 'Candidate Image',
    subtitle: 'Candidate photo capture',
  },
] as const;

function DashboardFallback() {
  return (
    <main className={styles.page}>
      <div className={styles.shell}>
        <div className={styles.loginLayout}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h1 className={styles.cardTitle}>Loading…</h1>
              <p className={styles.cardSubtitle}>Please wait.</p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

function DashboardContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [showNewBookingForm, setShowNewBookingForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);
  const [isLoadingJobRoles, setIsLoadingJobRoles] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [currentStage, setCurrentStage] = useState<1 | 2>(1);

  // New booking form state
  const [newBooking, setNewBooking] = useState({
    jobTitle: '',
    name: '',
    timeSlot: '',
    resume: null as File | null,
  });
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(null);
  const [capturedImages, setCapturedImages] = useState<string[]>([]);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState<Booking | null>(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [jobRoles, setJobRoles] = useState<{ id: string; title: string; createdAt: number }[]>([]);
  const [joiningRoomId, setJoiningRoomId] = useState<string | null>(null);
  const resumeInputRef = useRef<HTMLInputElement>(null);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Check authentication status on mount
  useEffect(() => {
    checkSession();
    checkTestMode();
  }, []);

  // Show toast when redirected back from room (e.g. session expired, outside time window)
  useEffect(() => {
    const roomError = searchParams.get('room_error');
    if (!roomError) return;
    const messages: Record<string, string> = {
      session_missing: 'Please sign in again to join the interview.',
      session_invalid: 'Your session expired. Please sign in again.',
      booking_required: 'Invalid link. Please open the interview from your dashboard.',
      booking_not_found: 'Booking not found. It may have been cancelled.',
      booking_not_confirmed: 'This interview is not confirmed yet.',
      not_authorized: 'You are not authorized to join this interview.',
      outside_time_window: 'You can join from 15 minutes before until 60 minutes after your interview time.',
      invalid_time: 'Invalid interview time. Please contact support.',
    };
    const message = messages[roomError] || 'Could not enter the interview. Please try again.';
    toast.error(message);
    setJoiningRoomId(null);
    router.replace('/', { scroll: false });
  }, [searchParams, router]);

  // Update current time every minute for join button enable/disable
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  const checkTestMode = async () => {
    try {
      const response = await fetch('/api/auth/test-mode');
      const data = await response.json();
      setTestMode(data.testMode || false);
      if (data.testMode) {
        // Auto-fill admin email in test mode
        setEmail('admin@10xds.com');
      }
    } catch (error) {
      // Ignore error, test mode check is optional
    }
  };

  // Fetch bookings and job roles when authenticated (initial load)
  useEffect(() => {
    if (isAuthenticated) {
      fetchBookings();
      fetchJobRoles();
    }
  }, [isAuthenticated]);

  // Check admin status whenever authentication state changes
  useEffect(() => {
    if (!isAuthenticated) {
      setIsAdminUser(false);
      return;
    }

    let cancelled = false;

    const checkAdmin = async () => {
      try {
        const adminCheck = await fetch('/api/auth/check-admin', {
          credentials: 'include',
        });
        if (!adminCheck.ok) {
          if (!cancelled) setIsAdminUser(false);
          return;
        }
        const adminData = await adminCheck.json();
        if (!cancelled) {
          setIsAdminUser(!!adminData.isAdmin);
        }
      } catch {
        if (!cancelled) setIsAdminUser(false);
      }
    };

    checkAdmin();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Background refresh every 30 seconds without affecting UX
  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    const refreshData = async () => {
      try {
        // Only refresh when tab is visible to avoid unnecessary work
        if (typeof document !== 'undefined' && document.hidden) {
          return;
        }

        const [bookingsResp, jobRolesResp] = await Promise.all([
          fetch('/api/bookings', { credentials: 'include' }),
          fetch('/api/job-roles', { credentials: 'include' }),
        ]);

        if (!cancelled && bookingsResp.ok) {
          const data = await bookingsResp.json();
          setBookings(
            (data.bookings || []).sort((a: Booking, b: Booking) => {
              if (a.status === 'confirmed' && b.status !== 'confirmed') return -1;
              if (b.status === 'confirmed' && a.status !== 'confirmed') return 1;
              return b.createdAt - a.createdAt;
            }),
          );
        }

        if (!cancelled && jobRolesResp.ok) {
          const data = await jobRolesResp.json();
          setJobRoles(data.jobRoles || []);
        }
      } catch {
        // Ignore background refresh errors to avoid disrupting the user
      }
    };

    const intervalId = setInterval(refreshData, 30000); // 30 seconds

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [isAuthenticated]);

  const checkSession = async () => {
    try {
      const response = await fetch('/api/auth/session', {
        credentials: 'include',
      });
      const data = await response.json();
      setIsAuthenticated(data.authenticated);
      if (data.authenticated) {
        setEmail(data.email);
      }
    } catch (error) {
      setIsAuthenticated(false);
    }
  };

  const fetchBookings = async () => {
    setIsLoadingBookings(true);
    try {
      const response = await fetch('/api/bookings', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        // Sort bookings: confirmed first, then by creation date (newest first)
        const sortedBookings = (data.bookings || []).sort((a: Booking, b: Booking) => {
          // Confirmed bookings first
          if (a.status === 'confirmed' && b.status !== 'confirmed') return -1;
          if (b.status === 'confirmed' && a.status !== 'confirmed') return 1;
          // Then by creation date (newest first)
          return b.createdAt - a.createdAt;
        });
        setBookings(sortedBookings);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setIsLoadingBookings(false);
    }
  };

  const fetchJobRoles = async () => {
    setIsLoadingJobRoles(true);
    try {
      const response = await fetch('/api/job-roles', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setJobRoles(data.jobRoles || []);
      }
    } catch (error) {
      console.error('Error fetching job roles:', error);
    } finally {
      setIsLoadingJobRoles(false);
    }
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/auth/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
        credentials: 'include',
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'OTP sent! Check console for development.');
        setShowOtpInput(true);
      } else {
        toast.error(data.error || 'Failed to send OTP');
      }
    } catch (error) {
      toast.error('Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleTestLogin = async () => {
    if (!testMode) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: 'admin@10xds.com' }),
        credentials: 'include',
      });

      const data = await response.json();
      if (response.ok) {
        toast.success('Test login successful!');
        // Re-check session to ensure cookie is set
        await checkSession();
        setShowOtpInput(false);
        setOtp('');
        
        // Check if user is admin
        const adminCheck = await fetch('/api/auth/check-admin', {
          credentials: 'include',
        });
        const adminData = await adminCheck.json();
        setIsAdminUser(!!adminData.isAdmin);
        if (adminData.isAdmin) {
          router.push('/admin');
          return;
        }
      } else {
        toast.error(data.error || 'Test login failed');
      }
    } catch (error) {
      toast.error('Failed to login');
    } finally {
      setLoading(false);
    }
  };

  const handleUseAnotherEmail = () => {
    setShowOtpInput(false);
    setOtp('');
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    // In test mode with admin@10xds.com, skip OTP requirement
    if (testMode && email.toLowerCase().trim() === 'admin@10xds.com') {
      await handleTestLogin();
      return;
    }

    if (!otp) {
      toast.error('Please enter the OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
        credentials: 'include',
      });

      const data = await response.json();
      if (response.ok) {
        toast.success('Login successful!');
        // Re-check session to ensure cookie is set
        await checkSession();
        setShowOtpInput(false);
        setOtp('');

        // Check if user is admin
        const adminCheck = await fetch('/api/auth/check-admin', {
          credentials: 'include',
        });
        const adminData = await adminCheck.json();
        setIsAdminUser(!!adminData.isAdmin);
        if (adminData.isAdmin) {
          router.push('/admin');
          return;
        }
      } else {
        toast.error(data.error || 'Invalid OTP');
      }
    } catch (error) {
      toast.error('Failed to verify OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      setIsAuthenticated(false);
      setEmail('');
      setBookings([]);
      setShowOtpInput(false);
      setIsAdminUser(false);
      setShowUserMenu(false);
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const resetBookingForm = () => {
    setNewBooking({ jobTitle: '', name: '', timeSlot: '', resume: null });
    setSelectedDateTime(null);
    // Reset the file input value
    if (resumeInputRef.current) {
      resumeInputRef.current.value = '';
    }
  };

  const handleToggleNewBookingForm = () => {
    if (showNewBookingForm) {
      // Closing the form - reset it
      resetBookingForm();
      setCurrentStage(1); // Reset to stage 1 when closing
    }
    setShowNewBookingForm(!showNewBookingForm);
  };

  const handleNewBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBooking.jobTitle || !newBooking.name || !selectedDateTime || !newBooking.resume) {
      toast.error('Please fill in all fields and upload a resume');
      return;
    }

    // Preserve form data and move to Stage 2
    toast.success('Details saved! Now capture your photo.');
    setShowNewBookingForm(false);
    
    // Move to Stage 2 (form data is preserved in state)
    setCurrentStage(2);
  };

  const handleRequestTimeChange = async (bookingId: string, requestedTimeSlot: string) => {
    if (!requestedTimeSlot) {
      toast.error('Please enter a new time slot');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/bookings/${bookingId}/request-change`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestedTimeSlot }),
        credentials: 'include',
      });

      const data = await response.json();
      if (response.ok) {
        toast.success('Time change request submitted!');
        fetchBookings();
      } else {
        toast.error(data.error || 'Failed to submit time change request');
      }
    } catch (error) {
      toast.error('Failed to submit time change request');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatDateTime = (dateTimeString: string) => {
    try {
      return new Date(dateTimeString).toLocaleString();
    } catch {
      return dateTimeString;
    }
  };

  const goBackToStage1 = () => {
    setCurrentStage(1);
    setShowNewBookingForm(true); // Show the form again when going back
  };

  const canJoinInterview = (timeSlot: string): boolean => {
    try {
      const interviewTime = new Date(timeSlot).getTime();
      const fifteenMinutesBefore = interviewTime - 15 * 60 * 1000; // 15 minutes in milliseconds
      const sixtyMinutesAfter = interviewTime + 60 * 60 * 1000; // 60 minutes in milliseconds
      
      return currentTime >= fifteenMinutesBefore && currentTime <= sixtyMinutesAfter;
    } catch {
      return false;
    }
  };

  const handleJoinInterview = (bookingId: string) => {
    const roomName = generateRoomId();
    setJoiningRoomId(bookingId);
    router.push(`/rooms/${roomName}?bookingId=${encodeURIComponent(bookingId)}`);
  };

  // Component to render Join Interview button with matching width
  const JoinInterviewButton = ({ booking }: { booking: Booking }) => {
    const referenceButtonsRef = useRef<HTMLDivElement>(null);
    const joinButtonRef = useRef<HTMLButtonElement>(null);
    const isJoining = joiningRoomId === booking.id;

    useEffect(() => {
      if (referenceButtonsRef.current && joinButtonRef.current) {
        const referenceWidth = referenceButtonsRef.current.offsetWidth;
        joinButtonRef.current.style.width = `${referenceWidth}px`;
      }
    }, []);

    return (
      <>
        <div
          ref={referenceButtonsRef}
          style={{
            display: 'flex',
            gap: '0.75rem',
            flexShrink: 0,
            position: 'absolute',
            visibility: 'hidden',
            pointerEvents: 'none',
          }}
        >
          <a
            href={booking.resumePath}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.secondaryButton}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
            }}
          >
            View resume
          </a>
          <button
            type="button"
            className={styles.dangerButton}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
            }}
          >
            Cancel
          </button>
        </div>
        <button
          ref={joinButtonRef}
          type="button"
          onClick={() => handleJoinInterview(booking.id)}
          disabled={isJoining}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.875rem',
            background: isJoining 
              ? 'linear-gradient(135deg, #9ca3af, #6b7280)' 
              : 'linear-gradient(135deg, #7c3aed, #4f46e5)',
            color: '#ffffff',
            border: 'none',
            borderRadius: '0.6rem',
            cursor: isJoining ? 'wait' : 'pointer',
            fontWeight: 500,
            transition: 'background-color 0.15s ease, transform 0.05s ease, box-shadow 0.15s ease',
            flexShrink: 0,
            opacity: isJoining ? 0.8 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
          }}
          onMouseEnter={(e) => {
            if (!isJoining) {
              e.currentTarget.style.background = 'linear-gradient(135deg, #6d28d9, #4338ca)';
              e.currentTarget.style.transform = 'translateY(-0.5px)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isJoining) {
              e.currentTarget.style.background = 'linear-gradient(135deg, #7c3aed, #4f46e5)';
              e.currentTarget.style.transform = 'translateY(0)';
            }
          }}
        >
          {isJoining && (
            <svg
              className={styles.spinner}
              style={{
                width: '16px',
                height: '16px',
              }}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray="32"
                strokeDashoffset="32"
                opacity="0.3"
              />
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray="32"
                strokeDashoffset="24"
              />
            </svg>
          )}
          {isJoining ? 'Joining...' : 'Join Interview'}
        </button>
      </>
    );
  };

  const handleCancelBooking = (booking: Booking) => {
    setBookingToCancel(booking);
    setCancelModalOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!bookingToCancel || !cancellationReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/bookings/${bookingToCancel.id}/cancel`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: cancellationReason.trim() }),
        credentials: 'include',
      });

      const data = await response.json();
      if (response.ok) {
        toast.success('Booking cancelled successfully');
        setCancelModalOpen(false);
        setBookingToCancel(null);
        setCancellationReason('');
        fetchBookings();
      } else {
        toast.error(data.error || 'Failed to cancel booking');
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error('Failed to cancel booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStage2Complete = async (images?: string[]) => {
    // Prevent duplicate submissions
    if (loading) {
      return;
    }

    // Use images from parameter if provided, otherwise fall back to state
    const imagesToUse = images || capturedImages;

    // Validate that we have all required data
    if (!newBooking.jobTitle || !newBooking.name || !selectedDateTime || !newBooking.resume) {
      toast.error('Missing form data. Please go back and fill in all fields.');
      return;
    }

    if (!imagesToUse || imagesToUse.length === 0) {
      toast.error('No images captured. Please try again.');
      return;
    }

    // Final check: Ensure the selected job role is still available
    // (This handles race conditions where a job role might get expired between selection and submission)
    const selectedJobRole = jobRoles.find(role => role.title === newBooking.jobTitle);
    if (!selectedJobRole) {
      toast.error('The selected job role is no longer available. Please select a different role.');
      // Reset to stage 1 so user can choose a different job role
      setCurrentStage(1);
      setShowNewBookingForm(true);
      return;
    }

    setLoading(true);
    try {
      // Create FormData with all booking data
      const formData = new FormData();
      formData.append('jobTitle', newBooking.jobTitle);
      formData.append('name', newBooking.name);
      // Format datetime in local timezone (YYYY-MM-DDTHH:mm format)
      const formatDateTimeLocal = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };
      formData.append('timeSlot', newBooking.timeSlot || (selectedDateTime ? formatDateTimeLocal(selectedDateTime) : ''));
      formData.append('resume', newBooking.resume);

      // Convert base64 images to Blobs and append to FormData
      imagesToUse.forEach((base64Image, index) => {
        // Convert base64 data URL to Blob
        const base64Data = base64Image.split(',')[1]; // Remove data:image/jpeg;base64, prefix
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/jpeg' });
        formData.append(`images`, blob, `image-${index}.jpg`);
      });

      // Call API to save everything
      const response = await fetch('/api/bookings/create-with-images', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Booking created successfully with photo!');
        // Clear form data after successful save
        setNewBooking({ jobTitle: '', name: '', timeSlot: '', resume: null });
        setSelectedDateTime(null);
        setCapturedImages([]);
        setCurrentStage(1);
        fetchBookings();
      } else {
        toast.error(data.error || 'Failed to create booking');
        // If duplicate booking (409 Conflict), redirect to dashboard
        if (response.status === 409) {
          // Reset form and return to dashboard view
          setNewBooking({ jobTitle: '', name: '', timeSlot: '', resume: null });
          setSelectedDateTime(null);
          setCapturedImages([]);
          setCurrentStage(1);
          setShowNewBookingForm(false);
          fetchBookings();
        } else {
          // Stay on Stage 2 so user can retry for other errors
        }
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Failed to create booking. Please try again.');
      // Stay on Stage 2 so user can retry
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (isAuthenticated === null) {
    return (
      <main className={styles.page}>
        <div className={styles.shell}>
          <div className={styles.loginLayout}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <h1 className={styles.cardTitle}>Checking your session…</h1>
                <p className={styles.cardSubtitle}>Please wait a moment while we prepare your dashboard.</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  // Login form
  if (!isAuthenticated) {
    return (
      <main className={styles.loginPage}>
        <div className={styles.loginFormPanel}>
          <div className={styles.loginFormCard}>
            <div className={styles.loginFormHeader}>
              <div className={styles.loginFormIconWrap}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20.5899 22C20.5899 18.13 16.7399 15 11.9999 15C7.25991 15 3.40991 18.13 3.40991 22" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <h2 className={styles.loginFormTitle}>
                  {showOtpInput ? 'Check your email' : 'Welcome back'}
                </h2>
                <p className={styles.loginFormSubtitle}>
                  {showOtpInput
                    ? `We sent a 6-digit code to ${email}`
                    : 'Sign in to access your interview dashboard'}
                </p>
              </div>
            </div>

            {testMode && (
              <div className={styles.testModeBanner}>
                <p className={styles.testModeTitle}>Test mode enabled</p>
                <p className={styles.testModeText}>
                  Log in directly as <strong>admin@10xds.com</strong> without an OTP.
                </p>
                <button
                  type="button"
                  onClick={handleTestLogin}
                  disabled={loading}
                  className={`${styles.primaryButton} ${styles.testModeButton}`}
                >
                  {loading ? 'Logging in…' : 'Test login (admin@10xds.com)'}
                </button>
              </div>
            )}

            <form className={styles.form} onSubmit={showOtpInput ? handleVerifyOTP : handleSendOTP}>
              <div className={styles.field}>
                <label htmlFor="email" className={styles.label}>Email address</label>
                <div className={styles.inputIconWrap}>
                  <svg className={styles.inputIcon} width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 6L12 13L2 6" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required={!testMode || email !== 'admin@10xds.com'}
                    disabled={showOtpInput && !testMode}
                    className={`${styles.input} ${styles.inputWithIcon}`}
                    placeholder="you@email.com"
                  />
                </div>
              </div>

              {showOtpInput && (!testMode || email.toLowerCase().trim() !== 'admin@10xds.com') && (
                <div className={styles.field}>
                  <label htmlFor="otp" className={styles.label}>One-time passcode</label>
                  <div className={styles.inputIconWrap}>
                    <svg className={styles.inputIcon} width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="11" width="18" height="11" rx="2" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <input
                      id="otp"
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      required={!testMode || email.toLowerCase().trim() !== 'admin@10xds.com'}
                      maxLength={6}
                      className={`${styles.input} ${styles.inputWithIcon} ${styles.inputOtp}`}
                      placeholder="• • • • • •"
                      inputMode="numeric"
                      autoFocus
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleUseAnotherEmail}
                    disabled={loading}
                    className={styles.linkButton}
                  >
                    ← Use a different email
                  </button>
                </div>
              )}

              {testMode && email.toLowerCase().trim() === 'admin@10xds.com' && !showOtpInput && (
                <p className={styles.helperText}>In test mode, you can log in directly without an OTP.</p>
              )}

              <button type="submit" disabled={loading} className={styles.primaryButton}>
                {loading
                  ? 'Please wait…'
                  : testMode && email.toLowerCase().trim() === 'admin@10xds.com' && !showOtpInput
                  ? 'Login (test mode)'
                  : showOtpInput
                  ? 'Verify & Sign in'
                  : 'Send one-time code'}
              </button>
            </form>

            {!showOtpInput && (
              <p className={styles.loginDisclaimer}>
                You will receive a one time pass code to your email.
              </p>
            )}
          </div>
        </div>
      </main>
    );
  }

  // Dashboard
  return (
    <main className={styles.page}>
      <div className={`${styles.shell} ${currentStage === 1 ? styles.stageShell : ''}`}>
        <header className={styles.brandBar}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              <Image 
                src="/images/10xds-logo.svg" 
                alt="10xDS Logo" 
                width={120} 
                height={58}
                priority
              />
            </div>
            <div className={styles.headerRight}>
              {isAdminUser && (
                <button
                  type="button"
                  className={styles.secondaryButton}
                  style={{ padding: '0.5rem 0.9rem', fontSize: '0.8rem' }}
                  onClick={() => router.push('/admin')}
                >
                  Admin dashboard
                </button>
              )}
              <div
                className={styles.userInfo}
                role="button"
                tabIndex={0}
                onClick={() => setShowUserMenu((prev) => !prev)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setShowUserMenu((prev) => !prev);
                  }
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className={styles.userEmail}>{email}</span>
              </div>
              {showUserMenu && (
                <div className={styles.userMenu}>
                  <button
                    type="button"
                    className={styles.userMenuItemDanger}
                    onClick={handleLogout}
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

      {currentStage === 2 ? (
        <div className={`${styles.shell} ${styles.stageShell}`}>
          <section className={`${styles.card} ${styles.cardStage2}`}>
            <div className={styles.stepper}>
              {STAGE_STEPS.map((step) => (
                <div
                  key={step.id}
                  className={`${styles.step} ${
                    step.id === currentStage ? styles.stepActive : styles.stepDisabled
                  }`}
                >
                  <div className={styles.stepBadge}>{step.id}</div>
                  <div className={styles.stepTexts}>
                    <span className={styles.stepTitle}>{step.title}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.stage2Content}>
              <Stage2Camera
                onBack={goBackToStage1}
                onComplete={(images: string[]) => handleStage2Complete(images)}
                onCaptureComplete={(images: string[]) => setCapturedImages(images)}
                isLoading={loading}
              />
            </div>
          </section>
        </div>
      ) : (
        <>

        <div className={styles.dashboardGrid}>
          <section className={styles.card}>
            <div className={styles.sectionTitleRow}>
              <div>
                <h2 className={styles.sectionTitle}>Book an interview</h2>
                <p className={styles.sectionHint}>
                  Enter your details to book an interview.
                </p>
              </div>
              {jobRoles.length > 0 && (
                <button
                  type="button"
                  className={`${styles.ghostButton} ${styles.toggleNewBooking}`}
                  onClick={handleToggleNewBookingForm}
                >
                  {showNewBookingForm ? 'Close form' : '+ New booking'}
                </button>
              )}
            </div>

            {showNewBookingForm && (
              <div className={styles.stepper}>
                {STAGE_STEPS.map((step) => (
                  <div
                    key={step.id}
                    className={`${styles.step} ${
                      step.id === currentStage ? styles.stepActive : styles.stepDisabled
                    }`}
                  >
                    <div className={styles.stepBadge}>{step.id}</div>
                    <div className={styles.stepTexts}>
                      <span className={styles.stepTitle}>{step.title}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {isLoadingJobRoles ? (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                color: '#6b7280',
                marginTop: '1rem',
              }}>
                <div style={{
                  display: 'inline-block',
                  width: '2rem',
                  height: '2rem',
                  border: '3px solid #e5e7eb',
                  borderTopColor: '#7c3aed',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}></div>
                <p style={{
                  fontSize: '0.875rem',
                  margin: '1rem 0 0 0',
                  color: '#6b7280',
                }}>
                  Loading job openings...
                </p>
              </div>
            ) : jobRoles.length === 0 ? (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                color: '#6b7280',
                marginTop: '1rem',
              }}>
                <p style={{
                  fontSize: '1rem',
                  margin: '0 0 0.5rem 0',
                  fontWeight: 500,
                  color: '#374151',
                }}>
                  No Job Openings Available
                </p>
                <p style={{
                  fontSize: '0.875rem',
                  margin: 0,
                  color: '#6b7280',
                }}>
                  There are currently no job openings available. Please check back later or contact HR for more information.
                </p>
              </div>
            ) : (
              showNewBookingForm ? (
                <div className={styles.newBookingCard}>
                  <form className={styles.form} onSubmit={handleNewBookingSubmit}>
                    <div className={styles.field}>
                      <div className={styles.labelRow}>
                        <label htmlFor="jobTitle" className={styles.label}>
                          Job title *
                </label>
                      </div>
                <select
                  id="jobTitle"
                  value={newBooking.jobTitle}
                        onChange={(e) =>
                          setNewBooking({ ...newBooking, jobTitle: e.target.value })
                        }
                  required
                        className={styles.select}
                >
                  <option value="">Select a job title</option>
                  {jobRoles.map((role) => (
                    <option key={role.id} value={role.title}>
                      {role.title}
                    </option>
                  ))}
                </select>
              </div>

                  <div className={styles.field}>
                    <div className={styles.labelRow}>
                      <label htmlFor="name" className={styles.label}>
                        Candidate name *
              </label>
                    </div>
              <input
                id="name"
                type="text"
                value={newBooking.name}
                onChange={(e) => setNewBooking({ ...newBooking, name: e.target.value })}
                required
                      className={styles.input}
                      placeholder="Full name"
              />
            </div>

                  <div className={styles.field}>
                    <div className={styles.labelRow}>
                      <label htmlFor="timeSlot" className={styles.label}>
                        Preferred date & time *
                      </label>
                      <span className={styles.labelHint}>Select interview date and time</span>
                    </div>
                    <DateTimePicker
                      selected={selectedDateTime}
                      onChange={(date) => {
                        setSelectedDateTime(date);
                        if (date) {
                          setNewBooking({ ...newBooking, timeSlot: date.toISOString() });
                        }
                      }}
                      minDate={new Date()}
                    />
                  </div>

                  <div className={styles.field}>
                    <div className={styles.labelRow}>
                      <label htmlFor="resume" className={styles.label}>
                        Resume upload *
                      </label>
                      <span className={styles.labelHint}>PDF or Word · up to 5MB</span>
                    </div>
                    <label htmlFor="resume" className={styles.fileLabel}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10 4V16M4 10H16"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                      {newBooking.resume ? newBooking.resume.name : 'Choose file'}
                    </label>
                    <input
                      ref={resumeInputRef}
                      id="resume"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setNewBooking({ ...newBooking, resume: file });
                      }}
                      required
                      className={styles.fileInput}
                    />
                    {newBooking.resume && (
                      <p className={styles.helperText}>
                        Selected: {newBooking.resume.name}
                      </p>
                    )}
                  </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className={styles.primaryButton}
                    >
                      {loading ? 'Please wait…' : 'Capture photo'}
                    </button>
                  </form>
                </div>
              ) : null
            )}
          </section>

          <section className={styles.card}>
            <div className={styles.sectionTitleRow}>
      <div>
                <h2 className={styles.sectionTitle}>Bookings</h2>
              </div>
            </div>

        {isLoadingBookings ? (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                color: '#6b7280',
              }}>
                <div style={{
                  display: 'inline-block',
                  width: '2rem',
                  height: '2rem',
                  border: '3px solid #e5e7eb',
                  borderTopColor: '#7c3aed',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}></div>
                <p style={{
                  fontSize: '0.875rem',
                  margin: '1rem 0 0 0',
                  color: '#6b7280',
                }}>
                  Loading bookings...
                </p>
              </div>
            ) : bookings.filter((booking) => booking.status !== 'cancelled').length === 0 ? (
              <p className={styles.bookingsEmpty}>
                No bookings available. Start by creating one on the left.
              </p>
            ) : (
              <div className={styles.bookingsList}>
                {bookings
                  .filter((booking) => booking.status !== 'cancelled')
                  .map((booking) => {
                    const statusClass =
                              booking.status === 'confirmed'
                        ? styles.statusConfirmed
                                : booking.status === 'completed'
                          ? styles.statusCompleted
                                  : booking.status === 'cancelled'
                            ? styles.statusCancelled
                            : styles.statusPending;

                  return (
                    <article key={booking.id} className={styles.bookingCard}>
                      <div className={styles.bookingTop}>
                        <div className={styles.bookingMeta}>
                          <h3 className={styles.bookingTitle}>{booking.jobTitle}</h3>
                          <p className={styles.bookingText}>
                            <span className={styles.bookingLabel}>Candidate:&nbsp;</span>
                            {booking.name}
                          </p>
                          <p className={styles.bookingText}>
                            <span className={styles.bookingLabel}>Time slot:&nbsp;</span>
                            {formatDateTime(booking.timeSlot)}
                          </p>
                          <p className={styles.bookingText}>
                            <span className={styles.bookingLabel}>Status:&nbsp;</span>
                            <span className={`${styles.statusChip} ${statusClass}`}>
                        {booking.status.toUpperCase()}
                      </span>
                    </p>
                    {booking.timeChangeRequest && (
                            <p className={styles.bookingText}>
                              <span className={styles.bookingLabel}>
                                Time change request:&nbsp;
                              </span>
                              {formatDateTime(
                                booking.timeChangeRequest.requestedTimeSlot,
                              )}{' '}
                              ({booking.timeChangeRequest.status})
                      </p>
                    )}
                          <p className={styles.metaSecondary}>
                            Created {formatDate(booking.createdAt)}
                    </p>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {booking.status === 'confirmed' && canJoinInterview(booking.timeSlot) && (
                      <JoinInterviewButton booking={booking} />
                    )}
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <a
                        href={booking.resumePath}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.secondaryButton}
                        style={{
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem',
                          textAlign: 'center',
                        }}
                      >
                        View resume
                      </a>
                      <button
                        type="button"
                        onClick={() => handleCancelBooking(booking)}
                        className={styles.dangerButton}
                        style={{
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>

                <TimeChangeRequestForm
                  bookingId={booking.id}
                  onSubmit={handleRequestTimeChange}
                  loading={loading}
                  hasExistingRequest={!!booking.timeChangeRequest}
                  existingRequestStatus={booking.timeChangeRequest?.status}
                />
                    </article>
                  );
                })}
              </div>
            )}
          </section>
          </div>
      </>
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      {cancelModalOpen && bookingToCancel && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
          }}
          onClick={() => {
            setCancelModalOpen(false);
            setBookingToCancel(null);
            setCancellationReason('');
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              padding: '2rem',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.5rem', color: '#1d1d1f' }}>
              Cancel Booking
            </h2>
            <p style={{ marginBottom: '1.5rem', color: '#666' }}>
              Are you sure you want to cancel this booking? This action cannot be undone.
            </p>
            <div style={{ marginBottom: '1.5rem' }}>
              <label
                htmlFor="cancellation-reason"
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: '#1d1d1f',
                }}
              >
                Reason for cancellation <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                id="cancellation-reason"
                value={cancellationReason}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 500) {
                    setCancellationReason(value);
                  }
                }}
                placeholder="Please provide a reason for cancelling this booking..."
                required
                rows={4}
                maxLength={500}
                className={styles.cancellationTextarea}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  color: '#6b7280',
                  backgroundColor: '#f9fafb',
                }}
              />
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                fontSize: '0.75rem', 
                color: cancellationReason.length >= 450 ? '#ef4444' : '#9ca3af',
                marginTop: '0.25rem',
                fontWeight: cancellationReason.length >= 450 ? 500 : 400
              }}>
                {cancellationReason.length}/500 characters
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => {
                  setCancelModalOpen(false);
                  setBookingToCancel(null);
                  setCancellationReason('');
                }}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  color: '#1d1d1f'
                }}
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleConfirmCancel}
                disabled={loading || !cancellationReason.trim()}
                style={{
                  padding: '0.5rem 1rem',
                  border: 'none',
                  borderRadius: '0.375rem',
                  background: loading || !cancellationReason.trim() ? '#9ca3af' : '#ef4444',
                  color: 'white',
                  cursor: loading || !cancellationReason.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                {loading ? 'Cancelling...' : 'Cancel Booking'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function TimeChangeRequestForm({
  bookingId,
  onSubmit,
  loading,
  hasExistingRequest = false,
  existingRequestStatus,
}: {
  bookingId: string;
  onSubmit: (bookingId: string, requestedTimeSlot: string) => void;
  loading: boolean;
  hasExistingRequest?: boolean;
  existingRequestStatus?: 'pending' | 'approved' | 'rejected';
}) {
  const [showForm, setShowForm] = useState(false);
  const [requestedTimeSlot, setRequestedTimeSlot] = useState<Date | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!requestedTimeSlot) {
      toast.error('Please select a date and time');
      return;
    }
    // Convert Date to datetime-local format string (YYYY-MM-DDTHH:mm) in local timezone
    const year = requestedTimeSlot.getFullYear();
    const month = String(requestedTimeSlot.getMonth() + 1).padStart(2, '0');
    const day = String(requestedTimeSlot.getDate()).padStart(2, '0');
    const hours = String(requestedTimeSlot.getHours()).padStart(2, '0');
    const minutes = String(requestedTimeSlot.getMinutes()).padStart(2, '0');
    const timeSlotString = `${year}-${month}-${day}T${hours}:${minutes}`;
    onSubmit(bookingId, timeSlotString);
    setShowForm(false);
    setRequestedTimeSlot(null);
  };

  if (!showForm) {
    const tooltipText = hasExistingRequest 
      ? `You can only request a time change once. Your previous request is currently ${existingRequestStatus}.`
      : '';

    return (
      <div 
        style={{ position: 'relative', marginTop: '0.75rem', display: 'inline-block' }}
        onMouseEnter={() => hasExistingRequest && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <button
          type="button"
          onClick={() => !hasExistingRequest && setShowForm(true)}
          disabled={hasExistingRequest}
          className={styles.secondaryButton}
          style={{
            opacity: hasExistingRequest ? 0.6 : 1,
            cursor: hasExistingRequest ? 'not-allowed' : 'pointer',
            pointerEvents: hasExistingRequest ? 'none' : 'auto',
          }}
        >
          Request time change
        </button>
        {showTooltip && hasExistingRequest && (
          <div
            style={{
              position: 'absolute',
              bottom: '100%',
              left: 0,
              marginBottom: '0.5rem',
              padding: '0.625rem 0.875rem',
              borderRadius: '0.5rem',
              background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
              border: '1px solid #fbbf24',
              color: '#92400e',
              fontSize: '0.8125rem',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              zIndex: 1000,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              pointerEvents: 'none',
            }}
          >
            ⚠️ {tooltipText}
          </div>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={styles.timeChangeForm}>
      <div
        style={{
          padding: '0.625rem 0.875rem',
          borderRadius: '0.5rem',
          background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
          border: '1px solid #60a5fa',
          color: '#1e40af',
          fontSize: '0.8125rem',
          fontWeight: 500,
          marginBottom: '0.75rem',
        }}
      >
        ⓘ You can only request a time change once. Please choose your preferred time carefully.
      </div>
      <label
        htmlFor={`timeChange-${bookingId}`}
        className={styles.label}
      >
        New preferred time slot
      </label>
      <div className={styles.timeChangeRow}>
        <DateTimePicker
          selected={requestedTimeSlot}
          onChange={(date) => setRequestedTimeSlot(date)}
          minDate={new Date()}
        />
        <div className={styles.timeChangeButtons}>
        <button
          type="submit"
          disabled={loading || !requestedTimeSlot}
            className={styles.primaryButton}
          >
            {loading ? 'Submitting…' : 'Submit'}
        </button>
        <button
          type="button"
          onClick={() => {
            setShowForm(false);
            setRequestedTimeSlot(null);
          }}
            className={styles.ghostButton}
        >
          Cancel
        </button>
        </div>
      </div>
    </form>
  );
}

export default function Page() {
  return (
    <Suspense fallback={<DashboardFallback />}>
      <DashboardContent />
    </Suspense>
  );
}
